import { TFunction } from 'i18next';
import { useLang } from 'hooks/lang';
import { isThisMonth, isThisWeek, isToday } from 'date-fns';

import { FullProject, isProjectActive, projectStatusByCode } from 'entities';

import { Card, Link, Table } from 'components/Blocks';
import { UserAvatar } from 'components/UserAvatar';
import { EntityStatusTag } from 'components/Entities';

import { ExpectedDate } from '../components';

import { getShortMonth, getTwoDigitYear } from 'utils/date';
import { cascadeEval } from 'utils/conditions';
import { bestEffortSortCompare, indexOfOrNull, sortResult } from 'utils/array';

interface ProjectsTableProps {
  projects: FullProject[];
  loading?: boolean;
}

function getExpectedDateLabel(expectedDate: string | undefined, t: TFunction) {
  if (!expectedDate) return 'N/A';

  const date = new Date(expectedDate);

  return cascadeEval([
    { is: isToday(date), then: t('today') },
    { is: isThisWeek(date), then: t('this-week') },
    { is: isThisMonth(date), then: t('this-month') },
    { is: true, then: `${getShortMonth(date)}/${getTwoDigitYear(date)}` },
  ]);
}

function sortExpectedLabel(labelA: string, labelB: string, t: TFunction) {
  const specialOrder = ['N/A', t('today'), t('this-week'), t('this-month')];

  const [specialIndexA, specialIndexB] = [labelA, labelB].map((label) =>
    indexOfOrNull(specialOrder, label),
  );

  if (specialIndexA !== null && specialIndexB !== null) return specialIndexA - specialIndexB;
  if (specialIndexA !== null) return sortResult('a', 'up');
  if (specialIndexB !== null) return sortResult('b', 'up');

  // none are special
  return bestEffortSortCompare(labelA, labelB);
}

export function ProjectsTable({ projects, loading }: ProjectsTableProps) {
  const { t } = useLang();

  return (
    <Card>
      <Table
        items={projects}
        loading={loading}
        itemsPerPage={15}
        uniqueKey="id"
        headerClassName="pb-3"
        columns={[
          {
            label: t('name'),
            property: 'name',
            search: true,
            sort: true,
            render: ({ name, id }) => (
              <Link to="projectProfile" params={{ projectId: id }} className="hover:underline">
                {name}
              </Link>
            ),
          },
          {
            label: t('client'),
            property: 'clientName',
            filter: true,
          },
          {
            label: t('ticket'),
            property: 'ticket',
            search: true,
          },
          {
            label: t('type'),
            filter: true,
            sort: true,
            render: (p) => t(p.type),
          },
          {
            label: t('owner'),
            filter: true,
            getTextValue: ({ owner }) => owner.name,
            render: ({ owner }) => (
              <UserAvatar user={owner} name nameClassName="text-sm" size={28} />
            ),
          },
          {
            label: t('expected-date'),
            getTextValue: ({ expectedDate }) => getExpectedDateLabel(expectedDate, t),
            filterSort: (a, b) => sortExpectedLabel(a, b, t),
            filter: true,
            render: ({ expectedDate, status }) => (
              <ExpectedDate date={expectedDate} isActive={isProjectActive(status)} />
            ),
          },
          {
            label: t('status'),
            filter: true,
            getTextValue: ({ status }) => projectStatusByCode[status].getLabel(t),
            render: ({ status }) => <EntityStatusTag target="project" status={status} />,
          },
        ]}
      />
    </Card>
  );
}
